import type { NavSection } from "~/composables/useContentRoute";

/**
 * Enforce a single active root highlight in the mega-menu.
 *
 * `@econnect/ui-megamenu` lights a root `<e-mega-menu-item>` whenever its panel
 * contains a link that matches the current route — and link matching is a PREFIX
 * match (`cur === target || cur.startsWith(target + "/")`). A cross-section link
 * (e.g. "API Documentatie" under *Partners* pointing at `/{locale}/docs/...`)
 * therefore lights the *Partners* trigger on any `/docs/*` page, on top of the
 * *Docs* trigger that legitimately owns that route — two highlights at once. The
 * element has no single-winner rule and no "force off" (its `active` attr only
 * adds), so we subtract the spurious one here.
 *
 * Rule (content-team requirement): the root section whose OWN `path` prefixes the
 * current route owns the highlight. We then reconcile both directions:
 *   - FORCE the owner active (`active` attr). The element can't light a panelled
 *     root on its own section index — the trigger has no `to`, and the index page
 *     matches no child link (children are `/{section}/*`, the "Read more" view-all
 *     is a plain button) — so `data-descendant-active` never fires there.
 *   - DIM every other root that lit only via a cross-section link (strip its
 *     `data-descendant-active`).
 * If no root path prefixes the route we can't arbitrate, so we leave the
 * element's default behaviour untouched.
 *
 * `active` is author-unused on these items (MegaNavItem never sets it), so we own
 * it outright and clear our prior force each pass — no stale highlight when the
 * owner changes. `data-descendant-active` is element-owned and re-derived from
 * scratch on every navigation, so stripping it stays a pure per-nav subtraction.
 * A `MutationObserver` on that attribute is the trigger (the element sets it in a
 * microtask after the history patch fires, before Vue's route watcher would run),
 * with `route.path` as a backstop.
 */
export function useNavHighlightDedupe(
  megaRef: Ref<HTMLElement | undefined>,
  sections: Ref<NavSection[]>,
) {
  const route = useRoute();

  // Longest root section path that prefixes the current route, or null.
  function ownerPath(path: string): string | null {
    let best: string | null = null;
    for (const s of sections.value) {
      const p = s.path;
      if (!p) continue;
      if (path === p || path.startsWith(p + "/")) {
        if (!best || p.length > best.length) best = p;
      }
    }
    return best;
  }

  let enforcing = false;

  function enforce() {
    const el = megaRef.value;
    if (!el) return;
    const owner = ownerPath(route.path);
    enforcing = true;
    try {
      const items = el.querySelectorAll<HTMLElement>(
        ":scope > e-mega-menu-item[data-section-path]",
      );
      for (const item of items) {
        // Clear our prior force so an owner change never strands a highlight.
        if (item.hasAttribute("active")) item.removeAttribute("active");
        if (!owner) continue;
        if (item.dataset.sectionPath === owner) {
          item.setAttribute("active", "true");
        } else if (item.hasAttribute("data-descendant-active")) {
          item.removeAttribute("data-descendant-active");
        }
      }
    } finally {
      enforcing = false;
    }
  }

  let obs: MutationObserver | undefined;

  onMounted(() => {
    void customElements.whenDefined("e-mega-menu-item").then(async () => {
      await nextTick();
      const el = megaRef.value;
      if (!el) return;
      obs = new MutationObserver(() => {
        if (enforcing) return;
        enforce();
      });
      obs.observe(el, {
        attributes: true,
        attributeFilter: ["data-descendant-active"],
        subtree: true,
      });
      enforce();
    });
  });

  watch(() => route.path, () => enforce());

  onUnmounted(() => obs?.disconnect());
}
